import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RefreshCw, Trash2, Clock, CheckCircle, XCircle, Loader2, PlayCircle, AlertTriangle, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

const TasksDashboard = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await api.get('/tasks', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Auto-refresh every 3 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchTasks, 3000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchTasks]);

  const cancelTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error cancelling task:', error);
    }
  };

  const retryFailedBadges = async (task) => {
    setRetrying(task.task_id);
    try {
      await api.post(`/events/${task.event_id}/badges/retry-failed`, {
        task_id: task.task_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error retrying badges:', error);
      alert('Failed to retry: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRetrying(null);
    }
  };

  const retryFailedWhatsApp = async (task) => {
    setRetrying(task.task_id);
    try {
      await api.post(`/events/${task.event_id}/badges/retry-failed-send`, {
        task_id: task.task_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error retrying WhatsApp send:', error);
      alert('Failed to retry: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRetrying(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed_with_errors': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'cancelled': return <XCircle className="h-4 w-4 text-gray-500" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      completed_with_errors: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const label = status === 'completed_with_errors' ? 'Completed with errors' : status;
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {label}
      </Badge>
    );
  };

  const getTaskTypeLabel = (type) => {
    const labels = {
      'generate_badge': 'Badge Generation',
      'generate_bulk_badges': 'Bulk Badge Generation',
      'retry_bulk_badges': 'Retry Badge Generation',
      'send_badge_email': 'Send Email',
      'send_bulk_notifications': 'Bulk Notifications',
      'bulk_send_whatsapp': 'Bulk WhatsApp Send',
      'retry_bulk_send_whatsapp': 'Retry WhatsApp Send',
      'import_attendees': 'Import Attendees'
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const hasFailedAttendees = (task) => {
    return task.failed_attendees && task.failed_attendees.length > 0;
  };

  const canRetry = (task) => {
    return (task.status === 'completed_with_errors' || task.status === 'failed') && hasFailedAttendees(task);
  };

  const isWhatsAppTask = (task) => {
    return task.task_type?.includes('whatsapp') || task.task_type?.includes('send');
  };

  const runningTasks = tasks.filter(t => t.status === 'running' || t.status === 'pending');

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="tasks-dashboard">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Background Tasks</h1>
            <p className="text-gray-500">Monitor badge generation, notifications, and imports</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchTasks} data-testid="refresh-tasks">
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Running Tasks */}
        {runningTasks.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                Running Tasks ({runningTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runningTasks.map(task => (
                <div key={task.task_id} className="bg-white rounded-lg p-4 mb-2 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-medium">{getTaskTypeLabel(task.task_type)}</span>
                      {getStatusBadge(task.status)}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelTask(task.task_id)}
                      className="text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {task.total > 0 && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Progress</span>
                        <span>{task.progress || 0} / {task.total}</span>
                      </div>
                      <Progress value={((task.progress || 0) / task.total) * 100} className="h-2" />
                    </div>
                  )}
                  {task.results && (
                    <div className="text-xs text-gray-600 mt-2">
                      {task.results.generated !== undefined && <span className="mr-3">Generated: {task.results.generated}</span>}
                      {task.results.failed !== undefined && task.results.failed > 0 && <span className="text-red-600">Failed: {task.results.failed}</span>}
                      {task.results.whatsapp_sent !== undefined && <span className="mr-3">Sent: {task.results.whatsapp_sent}</span>}
                      {task.results.whatsapp_failed !== undefined && task.results.whatsapp_failed > 0 && <span className="text-red-600">Failed: {task.results.whatsapp_failed}</span>}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-2">
                    Started: {formatDate(task.created_at)}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* All Tasks Table */}
        <Card>
          <CardHeader>
            <CardTitle>Task History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No background tasks yet. Tasks will appear here when you generate badges in bulk or send notifications.
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map(task => (
                  <div key={task.task_id} className="border rounded-lg overflow-hidden">
                    {/* Task Row */}
                    <div 
                      className={`p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${hasFailedAttendees(task) ? 'bg-yellow-50' : ''}`}
                      onClick={() => setExpandedTask(expandedTask === task.task_id ? null : task.task_id)}
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(task.status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{getTaskTypeLabel(task.task_type)}</span>
                            {getStatusBadge(task.status)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(task.created_at)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Progress */}
                        {task.total > 0 && (
                          <div className="flex items-center gap-2">
                            <Progress value={((task.progress || 0) / task.total) * 100} className="w-20 h-2" />
                            <span className="text-sm text-gray-500 w-20">
                              {task.progress || 0}/{task.total}
                            </span>
                          </div>
                        )}
                        
                        {/* Results summary */}
                        <div className="text-sm w-40">
                          {task.results && (
                            <>
                              {task.results.generated !== undefined && (
                                <span className="text-green-600 mr-2">✓{task.results.generated}</span>
                              )}
                              {task.results.whatsapp_sent !== undefined && (
                                <span className="text-green-600 mr-2">✓{task.results.whatsapp_sent}</span>
                              )}
                              {(task.results.failed > 0 || task.results.whatsapp_failed > 0) && (
                                <span className="text-red-600">✗{task.results.failed || task.results.whatsapp_failed}</span>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {canRetry(task) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                isWhatsAppTask(task) ? retryFailedWhatsApp(task) : retryFailedBadges(task);
                              }}
                              disabled={retrying === task.task_id}
                              className="text-blue-600"
                            >
                              {retrying === task.task_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Retry Failed
                                </>
                              )}
                            </Button>
                          )}
                          {(task.status === 'running' || task.status === 'pending') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelTask(task.task_id);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          {hasFailedAttendees(task) && (
                            expandedTask === task.task_id ? 
                              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Failed Attendees */}
                    {expandedTask === task.task_id && hasFailedAttendees(task) && (
                      <div className="border-t bg-red-50 p-4">
                        <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          Failed Attendees ({task.failed_attendees.length})
                        </h4>
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-red-700">
                                <th className="pb-2">Ticket ID</th>
                                <th className="pb-2">Name</th>
                                <th className="pb-2">Phone</th>
                                <th className="pb-2">Error</th>
                                <th className="pb-2">Stage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {task.failed_attendees.map((fa, idx) => (
                                <tr key={idx} className="border-t border-red-200">
                                  <td className="py-2">{fa.ticket_id || '-'}</td>
                                  <td className="py-2">{fa.name || '-'}</td>
                                  <td className="py-2">{fa.phone_number || '-'}</td>
                                  <td className="py-2 text-red-600 max-w-xs truncate">{fa.error || '-'}</td>
                                  <td className="py-2">
                                    <Badge variant="outline" className="text-xs">
                                      {fa.stage || 'unknown'}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default TasksDashboard;
